import {useContext, useEffect, useState} from "react";
import {MapViewContext} from "../contexts/MapViewContext";
import './CategoryFilter.css';
import {IoFilterCircle, IoFilterCircleOutline} from 'react-icons/io5'
import {GoCheckCircle, GoCheckCircleFill} from "react-icons/go";
import {TagCountsGrouped} from "../models/TagCountsGrouped";

const CategoryFilter = ({
                            categoryCounts,
                            feedRef,
                        }: {
    categoryCounts: TagCountsGrouped;
    feedRef: React.RefObject<HTMLDivElement>;
}) => {

    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
    const [isCategoryFilterExpanded, setIsCategoryFilterExpanded] = useState(false);

    useEffect(() => {
        const el = feedRef.current;
        if (!el) return;

        const handleScroll = () => {
            if (isCategoryFilterExpanded) {
                setIsCategoryFilterExpanded(false);
            }
        };

        el.addEventListener("scroll", handleScroll);
        return () => el.removeEventListener("scroll", handleScroll);
    }, [feedRef, isCategoryFilterExpanded]);

    const {
        selectedTagIds,
        selectedCategoryIds,
        setSelectedTagIds,
        setSelectedCategoryIds,
    } = useContext(MapViewContext);

    const toggleCategory = (id: string) => {
        setExpandedCategories(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const selectCategory = (id: string) => {
        const newSelected = selectedCategoryIds.includes(id)
            ? selectedCategoryIds.filter((t) => t !== id)
            : [...selectedCategoryIds, id];

        const url = new URL(window.location.href);
        if (newSelected.length > 0) {
            url.searchParams.set("categories_ids", newSelected.join(","));
        } else {
            url.searchParams.delete("categories_ids");
        }
        window.history.replaceState(null, "", url.toString());
        setSelectedCategoryIds(newSelected);
    };

    const toggleTag = (id: string) => {
        const newSelected = selectedTagIds.includes(id)
            ? selectedTagIds.filter((t) => t !== id)
            : [...selectedTagIds, id];

        const url = new URL(window.location.href);
        if (newSelected.length > 0) {
            url.searchParams.set("tag_ids", newSelected.join(","));
        } else {
            url.searchParams.delete("tag_ids");
        }
        window.history.replaceState(null, "", url.toString());
        setSelectedTagIds(newSelected);
    };

    if (!categoryCounts) {
        return null;
    }

    const sortSubCategories = (subCategoryCounts: Record<string, { name: string; count: number; color: string; }>) => {
        return Object.entries(subCategoryCounts).sort(([, a], [, b]) => b.count - a.count);
    }

    const sortedCategories = Object.entries(categoryCounts).sort(([, a], [, b]) => {
        const totalA = Object.values(a.tags).reduce((sum, t) => sum + t.count, 0);
        const totalB = Object.values(b.tags).reduce((sum, t) => sum + t.count, 0);
        return totalB - totalA;
    });

    return (
        <div className="category-filter">
            <div className="category-filter-header" onClick={() => setIsCategoryFilterExpanded(prev => !prev)}>
                <h5>Categories</h5>
                <span className="chevron"> {isCategoryFilterExpanded ?
                    <IoFilterCircle style={{marginRight: 6}} size={18}/> :
                    <IoFilterCircleOutline style={{marginRight: 6}}/>}</span>
            </div>

                <div className={`category-filter-body ${
                    isCategoryFilterExpanded ? "expanded" : ""
                }`}>
                    <ul>
                        {sortedCategories.map(([categoryId, category]) => {
                            const isSelectedCategory = selectedCategoryIds.includes(categoryId);
                            if (isSelectedCategory) {
                                expandedCategories[categoryId] = false;
                            }
                            const isExpanded = expandedCategories[categoryId];
                            return (
                                <li key={categoryId}>
                                    <div className="category-name" onClick={() => toggleCategory(categoryId)}>
                                        <span>
                                            <button
                                                className={`category-pick-button ${isSelectedCategory ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // Prevent collapsing the category
                                                    selectCategory(categoryId);
                                                }}
                                                title="Toggle category selection"
                                            >
                                                {isSelectedCategory ? <GoCheckCircleFill/> :
                                                    <GoCheckCircle/>}
                                            </button>
                                            {category.name}{' '}
                                            <span className="count">
                                                ({Object.values(category.tags).reduce((sum, t) => sum + t.count, 0)})
                                            </span>
                                        </span>
                                        <span className="expand-toggle">{isExpanded ? '▲' : '▼'}</span>

                                    </div>

                                    {isExpanded && (
                                        <ul className="subcategory-list">
                                            {sortSubCategories(category.tags).map(([tagId, tag]) => (
                                                <li key={tagId}
                                                    className={selectedTagIds.includes(tagId) ? 'active' : ''}>
                                                    <button onClick={() => toggleTag(tagId)}>
                                                        <span className="bullet"
                                                              style={{backgroundColor: tag.color}}/> {tag.name}{' '}
                                                        <span className="count">({tag.count})</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
        </div>
    );
};

export default CategoryFilter;
